import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, forkJoin } from 'rxjs'

import {
  ConfigDefaults,
  ConfigResponse,
  ConfigVersion,
  Direction,
  MetricSpec,
  PillarKey,
  RuleMetric,
  SCORECARD_SCHEMA_VERSION,
  ScorecardParams,
  SimulationPopulation,
  SimulationRequest,
  SimulationResult,
  ThresholdUnit,
  TierPoints,
  TierThresholds,
} from '../../data/risk-scorecard-config.interface'
import { STATUS_UI, diffLabel, hasTiers, personName } from '../../data/risk-scorecard-config.labels'
import { ApiRiskScorecardConfigService } from '../../data/api-risk-scorecard-config.service'
import { SIMULATION_SAMPLE } from '../../data/risk-scorecard-simulation.view'
import { AuthService } from '@data/services/modules/auth/auth.service'

const PILLARS: PillarKey[] = ['character', 'capacity', 'capital']
//* La API compara las sumas con tolerancia: 100/3 · 3 no da 100 exacto en coma flotante.
const SUM_TOLERANCE = 1e-6

/**
 * Una versión de la configuración de la calculadora (plan v4.2 §7.2): `new` crea un borrador desde la activa (o desde
 * la versión de `?from=`, al duplicar), y `:id` muestra una versión. Solo un borrador se edita; activar o volver a una
 * archivada pide motivo (queda en el registro de activaciones). La API valida a fondo al guardar: aquí solo se avisa
 * de lo evidente (sumas y orden de los umbrales) para no llegar a ciegas.
 */
@Component({
  selector: 'app-risk-scorecard-config-detail',
  templateUrl: './risk-scorecard-config-detail.component.html',
  styleUrls: ['./risk-scorecard-config-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class RiskScorecardConfigDetailComponent implements OnInit {
  version: ConfigVersion | null = null
  defaults: ConfigDefaults | null = null
  params: ScorecardParams | null = null
  name = ''
  description = ''
  changeReason = ''
  derivedFromId: string | null = null
  derivedFromVersion: number | null = null

  loading = false
  loadError: string | null = null
  saving = false
  serverErrors: string[] = []
  activateVisible = false
  activateReason = ''
  advancedOpen = false

  //* Simulador (E4): la versión que se ve, o lo que se está editando sin guardar, contra la activa.
  simulateVisible = false
  simulating = false
  simulation: SimulationResult | null = null
  simulationError: string | null = null
  sampleSize: number = SIMULATION_SAMPLE.default
  readonly sampleLimits = SIMULATION_SAMPLE
  //* Plan v4.3 §9.4 y §2b · Sobre quién: la ficha de cada cliente, o los clientes nuevos con su preoferta decidida.
  population: SimulationPopulation = 'clients'
  readonly populations: ReadonlyArray<{ value: SimulationPopulation; label: string }> = [
    { value: 'clients', label: 'Clientes' },
    { value: 'new_clients', label: 'Clientes nuevos con preoferta' },
  ]

  readonly pillars = PILLARS
  readonly statusUi = STATUS_UI
  readonly personName = personName
  readonly hasTiers = hasTiers
  readonly canCreate: boolean
  readonly canUpdate: boolean
  readonly canActivate: boolean
  readonly canDelete: boolean
  readonly canSimulate: boolean
  readonly canOpenClient: boolean

  private readonly api = inject(ApiRiskScorecardConfigService)
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly confirm = inject(ConfirmationService)
  private readonly messages = inject(MessageService)
  private readonly cd = inject(ChangeDetectorRef)
  private readonly destroyRef = inject(DestroyRef)
  private savedSnapshot = ''

  constructor() {
    const auth = inject(AuthService)
    this.canCreate = auth.hasModulePermission('risk-scorecard-config', 'create')
    this.canUpdate = auth.hasModulePermission('risk-scorecard-config', 'update')
    this.canActivate = auth.hasModulePermission('risk-scorecard-config', 'activate')
    this.canDelete = auth.hasModulePermission('risk-scorecard-config', 'delete')
    this.canSimulate = auth.hasModulePermission('risk-scorecard-config', 'simulate')
    this.canOpenClient = auth.hasModulePermission('clients', 'get-by-id')
  }

  get isNew(): boolean {
    return !this.version
  }

  get editable(): boolean {
    if (this.isNew) return this.canCreate
    return !this.outdated && this.version!.status === 'draft' && !this.version!.isSystemDefault && this.canUpdate
  }

  /** Versión del motor 1 (sin `schemaVersion` 2): la API no la usa; queda para consulta (plan v4.3 T-03). */
  get outdated(): boolean {
    return !!this.params && this.params.schemaVersion !== SCORECARD_SCHEMA_VERSION
  }

  get reasonMin(): number {
    return this.defaults?.reasonMinLength ?? 10
  }

  get dirty(): boolean {
    return this.snapshot() !== this.savedSnapshot
  }

  get catalog() {
    return this.defaults?.catalog ?? null
  }

  /** Lo que se simula: la versión guardada si no hay cambios; si los hay (o es nueva), lo que está en pantalla. */
  get simulationLabel(): string {
    return this.version && !this.dirty ? `v${this.version.version}` : 'Sin guardar'
  }

  ngOnInit(): void {
    //* La ruta cambia sin recrear el componente (de una versión a otra, o de `new` a duplicar otra): se recarga en cada cambio.
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load())
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id')
    const from = this.route.snapshot.queryParamMap.get('from')
    this.loading = true
    this.loadError = null
    const base$ = id && id !== 'new' ? this.api.get(id) : from ? this.api.get(from) : this.api.active()
    forkJoin({ defaults: this.api.defaults(), base: base$ }).subscribe(({ defaults, base }) => {
      this.loading = false
      if (defaults.error || base.error) {
        const failed = defaults.error ? defaults : base
        this.loadError = failed.status === 403 ? 'No tienes permiso para ver la configuración de la calculadora.' : failed.msg
      } else this.hydrate(defaults.data!, base.data as ConfigVersion | null, !!id && id !== 'new')
      this.cd.markForCheck()
    })
  }

  // ── Estructura del formulario ───────────────────────────────────────────────

  /** Las métricas de primer nivel de un pilar (sin grupo, incluidos los grupos), en el orden de la semilla. */
  topLevel(pillar: PillarKey): string[] {
    return this.metricKeys().filter((key) => this.params!.metrics[key].pillar === pillar && !this.groupOf(key))
  }

  members(groupKey: string): string[] {
    return this.metricKeys().filter((key) => this.groupOf(key) === groupKey)
  }

  isGroup(key: string): boolean {
    return this.params?.metrics[key]?.kind === 'group'
  }

  label(key: string): string {
    return this.catalog?.metrics[key] ?? this.catalog?.groups[key] ?? key
  }

  pillarLabel(pillar: PillarKey): string {
    return this.catalog?.pillars[pillar] ?? pillar
  }

  unitOf(key: string): ThresholdUnit {
    return this.catalog?.units[key] ?? 'count'
  }

  ruleText(key: string): string | null {
    const spec = this.params?.metrics[key]
    return spec?.kind === 'rule' ? (this.catalog?.rules[spec.rule] ?? null) : null
  }

  extraKeys(key: string): string[] {
    const spec = this.params?.metrics[key] as RuleMetric
    return spec?.kind === 'rule' && spec.extra ? Object.keys(spec.extra) : []
  }

  extraLabel(name: string): string {
    return this.catalog?.extraTiers[name]?.label ?? name
  }

  extraUnit(name: string): ThresholdUnit {
    return this.catalog?.extraTiers[name]?.unit ?? 'count'
  }

  pillarSum(): number {
    return PILLARS.reduce((acc, pillar) => acc + Number(this.params?.pillars[pillar] ?? 0), 0)
  }

  levelSum(keys: string[]): number {
    return keys.reduce((acc, key) => acc + Number(this.params?.metrics[key]?.weight ?? 0), 0)
  }

  isHundred(sum: number): boolean {
    return Math.abs(sum - 100) <= SUM_TOLERANCE
  }

  vetoKeys(): string[] {
    return Object.keys(this.params?.vetoes ?? {})
  }

  trackByKey(_index: number, key: string): string {
    return key
  }

  // ── Edición ─────────────────────────────────────────────────────────────────

  //* El corte de Favorable es 200/3 (60 de 90 en el Excel): se muestra con 2 decimales, pero lo guardado no cambia
  //* mientras nadie edite el campo.
  band(value: number | null | undefined): number | null {
    return value === null || value === undefined ? null : Math.round(value * 100) / 100
  }

  setNumber(target: Record<string, any>, field: string, raw: unknown): void {
    target[field] = raw === '' || raw === null || raw === undefined ? null : Number(raw)
  }

  setWeight(key: string, raw: unknown): void {
    this.setNumber(this.params!.metrics[key], 'weight', raw)
  }

  setTiers(key: string, tiers: TierThresholds): void {
    ;(this.params!.metrics[key] as any).tiers = tiers
  }

  setTierPoints(key: string, tierPoints: TierPoints): void {
    ;(this.params!.metrics[key] as any).tierPoints = tierPoints
  }

  setDirection(key: string, direction: Direction): void {
    ;(this.params!.metrics[key] as any).direction = direction
  }

  setExtra(key: string, name: string, tiers: TierThresholds): void {
    ;(this.params!.metrics[key] as RuleMetric).extra![name] = tiers
  }

  setSevereKeys(raw: string): void {
    this.params!.severeObservationKeys = raw
      .split(/[\s,]+/)
      .map((item) => item.trim().toUpperCase())
      .filter(Boolean)
  }

  severeKeysText(): string {
    return (this.params?.severeObservationKeys ?? []).join(', ')
  }

  // ── Acciones ────────────────────────────────────────────────────────────────

  save(): void {
    if (!this.editable || this.saving || !this.params) return
    if (this.name.trim().length < 3) return this.toast('warn', 'Falta el nombre', 'Ponle un nombre a la versión (mínimo 3 caracteres).')
    if (this.changeReason.trim().length < this.reasonMin) return this.toast('warn', 'Falta el motivo', `Explica por qué se crea esta versión (mínimo ${this.reasonMin} caracteres).`)
    this.saving = true
    this.serverErrors = []
    const body = { name: this.name.trim(), description: this.description.trim(), changeReason: this.changeReason.trim(), params: this.params }
    const request$ = this.isNew ? this.api.create({ ...body, ...(this.derivedFromId ? { derivedFromId: this.derivedFromId } : {}) }) : this.api.update(this.version!._id, body)
    request$.subscribe((response) => {
      this.saving = false
      if (this.failed(response, 'No se guardó')) return
      this.toast('success', 'Borrador guardado', `La versión ${response.data!.version} quedó como borrador. Actívala cuando esté lista.`)
      if (this.isNew) this.router.navigate(['/risk-scorecard-config', response.data!._id])
      else this.hydrate(this.defaults!, response.data!, true)
      this.cd.markForCheck()
    })
  }

  /** Duplica la versión que se está viendo (no la activa) como un borrador nuevo. */
  duplicate(): void {
    if (!this.version) return
    this.router.navigate(['/risk-scorecard-config/new'], { queryParams: { from: this.version._id } })
  }

  openActivate(): void {
    this.activateReason = ''
    this.activateVisible = true
  }

  activate(): void {
    if (!this.version || this.saving || this.activateReason.trim().length < this.reasonMin) return
    this.saving = true
    this.api.activate(this.version._id, this.activateReason.trim()).subscribe((response) => {
      this.saving = false
      if (this.failed(response, 'No se activó')) return
      this.activateVisible = false
      this.toast('success', 'Versión activa', `La calculadora ya usa la versión ${response.data!.version}. Los arrendamientos ya sellados conservan la suya.`)
      this.hydrate(this.defaults!, response.data!, true)
      this.cd.markForCheck()
    })
  }

  remove(): void {
    if (!this.version || !this.canDelete || this.version.status !== 'draft' || this.version.isSystemDefault) return
    const version = this.version
    this.confirm.confirm({
      key: 'risk-config-detail',
      header: `Borrar el borrador v${version.version}`,
      message: `«${version.name}» se borra por completo.`,
      acceptLabel: 'Borrar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.api.remove(version._id).subscribe((response) => {
          if (this.failed(response, 'No se borró')) return
          this.router.navigate(['/risk-scorecard-config'])
        }),
    })
  }

  openSimulation(): void {
    this.simulateVisible = true
    this.runSimulation()
  }

  setPopulation(population: SimulationPopulation): void {
    if (population === this.population) return
    this.population = population
    this.simulation = null
    this.runSimulation()
  }

  runSimulation(): void {
    if (!this.params || this.simulating) return
    //* Campo vacío → la muestra por defecto; cualquier número (también 0) se acota a lo que acepta la API.
    const typed = this.sampleSize === null || (this.sampleSize as unknown) === '' ? NaN : Number(this.sampleSize)
    const sampleSize = Number.isFinite(typed) ? Math.min(this.sampleLimits.max, Math.max(this.sampleLimits.min, Math.round(typed))) : this.sampleLimits.default
    this.sampleSize = sampleSize
    const body: SimulationRequest = this.version && !this.dirty ? { configId: this.version._id, sampleSize } : { params: this.params, sampleSize }
    this.simulating = true
    this.simulationError = null
    this.serverErrors = []
    this.api.simulate(body, this.population).subscribe((response) => {
      this.simulating = false
      if (response.error) {
        this.simulation = null
        this.simulationError = response.status === 403 ? 'No tienes permiso para simular versiones.' : response.msg
        this.serverErrors = response.errors ?? []
      } else this.simulation = response.data
      this.cd.markForCheck()
    })
  }

  diffText(index: number): string {
    return diffLabel(this.version!.diff[index], this.catalog)
  }

  // ── Interno ─────────────────────────────────────────────────────────────────

  private hydrate(defaults: ConfigDefaults, base: ConfigVersion | null, isExisting: boolean): void {
    this.defaults = defaults
    this.serverErrors = []
    const params = JSON.parse(JSON.stringify(base?.params ?? defaults.params)) as ScorecardParams
    this.params = params
    if (isExisting && base) {
      this.version = base
      this.name = base.name
      this.description = base.description ?? ''
      this.changeReason = base.changeReason ?? ''
      this.derivedFromId = null
      this.derivedFromVersion = base.derivedFromVersion
    } else {
      this.version = null
      this.name = base ? `Copia de v${base.version}` : ''
      this.description = ''
      this.changeReason = ''
      this.derivedFromId = base?._id ?? null
      this.derivedFromVersion = base?.version ?? null
    }
    this.savedSnapshot = this.snapshot()
  }

  private metricKeys(): string[] {
    //* El orden lo da la semilla; lo que la versión traiga de más (no debería) va al final para no esconderlo.
    const seedOrder = Object.keys(this.defaults?.params.metrics ?? {})
    const own = Object.keys(this.params?.metrics ?? {})
    return [...seedOrder.filter((key) => own.includes(key)), ...own.filter((key) => !seedOrder.includes(key))]
  }

  private groupOf(key: string): string | null {
    const spec = this.params?.metrics[key] as MetricSpec & { group?: string }
    return spec && spec.kind !== 'group' ? (spec.group ?? null) : null
  }

  private snapshot(): string {
    return JSON.stringify({ params: this.params, name: this.name, description: this.description, changeReason: this.changeReason })
  }

  private failed(response: ConfigResponse<unknown>, summary: string): boolean {
    if (!response.error) return false
    this.serverErrors = response.errors ?? []
    this.toast('error', summary, response.msg)
    this.cd.markForCheck()
    return true
  }

  private toast(severity: 'success' | 'warn' | 'error', summary: string, detail: string): void {
    this.messages.add({ key: 'risk-config-detail', severity, summary, detail, life: severity === 'error' ? 8000 : 5000 })
  }
}
